import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from '@erp/shared';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSession } from '@/features/auth';
import { useUpdateProfile } from '@/features/users/api/use-security';
import { changeLocale } from '@/lib/i18n';
import { LanguageDropdown } from './language-dropdown';

export function LanguageToggle({
  compactOnMobile = false,
}: {
  readonly compactOnMobile?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const updateProfile = useUpdateProfile();
  const pendingLocale = useRef<SupportedLocale | null>(null);
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  const locale = isSupportedLocale(resolved) ? resolved : DEFAULT_LOCALE;

  useEffect(() => {
    const profileLocale = session.data?.locale;
    if (
      pendingLocale.current === null &&
      profileLocale !== undefined &&
      isSupportedLocale(profileLocale) &&
      profileLocale !== locale
    ) {
      changeLocale(profileLocale);
    }
  }, [locale, session.data?.locale]);

  const selectLocale = async (next: SupportedLocale): Promise<void> => {
    const previous = locale;
    pendingLocale.current = next;
    changeLocale(next);
    if (session.data === null || session.data === undefined) {
      pendingLocale.current = null;
      return;
    }
    try {
      await updateProfile.mutateAsync({ locale: next });
    } catch (error) {
      changeLocale(previous);
      toast.error(error instanceof Error ? error.message : t('language.saveError'));
    } finally {
      pendingLocale.current = null;
    }
  };

  return (
    <LanguageDropdown
      locale={locale}
      onLocaleChange={selectLocale}
      busy={updateProfile.isPending}
      compactOnMobile={compactOnMobile}
    />
  );
}
